import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { clsx } from 'clsx';
import {
  CheckCircle2,
  XCircle,
  Flag,
  Loader2,
  ExternalLink,
  MessageCircle,
} from 'lucide-react';
import { useReviewQueueStore } from '@/stores/reviewQueue';
import { useAuth } from '@/contexts/AuthContext';
import type { RawConceptData } from '@/services/api';

export function ReviewItem() {
  const {
    results,
    selectedId,
    sourceConcepts,
    conceptsLoading,
    patching,
    submitVerdict,
  } = useReviewQueueStore();

  const { user } = useAuth();
  const [note, setNote] = useState('');

  const selected = results.find((r) => r.id === selectedId) ?? null;

  // Reset note when the selection changes.
  useEffect(() => {
    setNote('');
  }, [selectedId]);

  if (!selected) {
    return (
      <div className="flex items-center justify-center h-full text-sm text-slate-400 p-8">
        {results.length === 0
          ? 'Inbox zero — no pending review items.'
          : 'Select a result to review.'}
      </div>
    );
  }

  const reviewerEmail = user?.email ?? undefined;
  const already = selected.human_reviewed;

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="px-5 py-3 border-b border-slate-200">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <DecisionBadge decision={selected.decision} />
          <span className="text-[10px] text-emerald-600 font-semibold">
            LLM confidence {(selected.confidence * 100).toFixed(0)}%
          </span>
          {selected.entity_type && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600 font-medium">
              {selected.entity_type}
            </span>
          )}
          {selected.human_verdict && (
            <VerdictBadge verdict={selected.human_verdict} />
          )}
          <code className="text-[10px] text-slate-400 ml-auto">
            {selected.id.slice(0, 8)}
          </code>
        </div>
        <div className="flex items-center gap-3 text-[10px] text-slate-500 flex-wrap">
          {selected.batch_id && <span>batch: {selected.batch_id}</span>}
          {selected.cluster_id && <span>cluster: {selected.cluster_id}</span>}
          {selected.model_used && <span>model: {selected.model_used}</span>}
          {selected.canonical_entity_id && (
            <Link
              to={`/knowledge/estate/entities/${selected.canonical_entity_id}`}
              className="ml-auto inline-flex items-center gap-0.5 text-brand-teal hover:text-brand-teal-dark"
            >
              canonical <ExternalLink size={10} />
            </Link>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto px-5 py-4 space-y-4">
        {/* Source concept side-by-side */}
        <div>
          <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">
            Source Concepts ({selected.source_entity_ids.length})
          </div>
          {conceptsLoading ? (
            <div className="text-xs text-slate-400">
              <Loader2 size={12} className="animate-spin inline mr-1" />
              Loading source concepts...
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              {selected.source_entity_ids.map((id) => {
                const c = sourceConcepts[id];
                return (
                  <SourceCard key={id} sourceId={id} concept={c} />
                );
              })}
            </div>
          )}
        </div>

        {/* LLM reasoning (full text) */}
        {selected.reasoning && (
          <div>
            <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">
              LLM Reasoning
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4 text-xs text-slate-700 whitespace-pre-wrap">
              {selected.reasoning}
            </div>
          </div>
        )}

        {/* Key factors + conflicting */}
        {(selected.key_factors?.length || 0) > 0 && (
          <div>
            <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">
              Key Factors
            </div>
            <div className="flex flex-wrap gap-1">
              {selected.key_factors.map((f, i) => (
                <span
                  key={i}
                  className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-700"
                >
                  {f}
                </span>
              ))}
            </div>
          </div>
        )}
        {selected.conflicting_evidence && selected.conflicting_evidence.length > 0 && (
          <div>
            <div className="text-[10px] font-bold text-rose-600 uppercase tracking-wider mb-1">
              Conflicting Evidence
            </div>
            <div className="flex flex-wrap gap-1">
              {selected.conflicting_evidence.map((f, i) => (
                <span
                  key={i}
                  className="text-[10px] px-1.5 py-0.5 rounded-full bg-rose-50 text-rose-700 border border-rose-200"
                >
                  {f}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Existing human note */}
        {selected.human_note && (
          <div className="text-xs bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
            <div className="flex items-center gap-1.5 mb-1">
              <MessageCircle size={11} className="text-slate-400" />
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                Existing reviewer note
                {selected.human_reviewed_by && ` — ${selected.human_reviewed_by}`}
              </span>
            </div>
            <p className="text-slate-700 whitespace-pre-wrap">{selected.human_note}</p>
          </div>
        )}
      </div>

      {/* Action bar */}
      <div className="border-t border-slate-200 bg-slate-50 px-5 py-3 space-y-2">
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Reviewer note (optional, audit trail)"
          rows={2}
          className="w-full text-xs bg-white border border-slate-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-brand-teal"
        />
        <div className="flex items-center gap-2">
          <button
            onClick={() =>
              submitVerdict('confirmed', { note, reviewerEmail })
            }
            disabled={patching}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 disabled:opacity-40"
          >
            {patching ? <Loader2 size={11} className="animate-spin" /> : <CheckCircle2 size={11} />}
            Confirm
          </button>
          <button
            onClick={() =>
              submitVerdict('overridden', { note, reviewerEmail })
            }
            disabled={patching}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold bg-rose-500 text-white rounded-lg hover:bg-rose-600 disabled:opacity-40"
          >
            <XCircle size={11} />
            Override
          </button>
          <button
            onClick={() =>
              submitVerdict('flagged', { note, reviewerEmail })
            }
            disabled={patching}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-40"
          >
            <Flag size={11} />
            Flag
          </button>
          {reviewerEmail ? (
            <span className="ml-auto text-[10px] text-slate-500">
              signed in as <span className="text-navy-900">{reviewerEmail}</span>
            </span>
          ) : (
            <span className="ml-auto text-[10px] text-amber-600">
              no reviewer identity — verdict will be stored without attribution
            </span>
          )}
        </div>
        {already && (
          <p className="text-[10px] text-slate-500 italic">
            This item has already been reviewed — submitting again will update
            the verdict.
          </p>
        )}
      </div>
    </div>
  );
}

function DecisionBadge({ decision }: { decision: string }) {
  const colors: Record<string, string> = {
    merge: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    link: 'bg-blue-50 text-blue-700 border-blue-200',
    distinct: 'bg-slate-100 text-slate-600 border-slate-200',
    promote: 'bg-violet-50 text-violet-700 border-violet-200',
  };
  return (
    <span
      className={clsx(
        'text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border',
        colors[decision] ?? 'bg-slate-50 text-slate-500 border-slate-200',
      )}
    >
      {decision}
    </span>
  );
}

function VerdictBadge({
  verdict,
}: {
  verdict: 'confirmed' | 'overridden' | 'flagged';
}) {
  const colors: Record<string, string> = {
    confirmed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    overridden: 'bg-rose-50 text-rose-700 border-rose-200',
    flagged: 'bg-amber-50 text-amber-700 border-amber-200',
  };
  return (
    <span
      className={clsx(
        'text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-full border',
        colors[verdict] ?? 'bg-slate-50 text-slate-500 border-slate-200',
      )}
    >
      human · {verdict}
    </span>
  );
}

function SourceCard({
  sourceId,
  concept,
}: {
  sourceId: string;
  concept?: RawConceptData;
}) {
  if (!concept) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-3">
        <div className="text-[10px] text-slate-400 font-mono">{sourceId}</div>
        <div className="text-sm text-slate-500 mt-1 italic">
          concept not fetched (may be archived or in a different store)
        </div>
      </div>
    );
  }
  const data = concept.data ?? {};
  const entries = Object.entries(data).slice(0, 10);
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-3">
      <div className="flex items-center justify-between gap-2 mb-1">
        <span className="text-sm font-semibold text-slate-800 truncate">
          {concept.name || concept.id.slice(0, 12)}
        </span>
        <Link
          to={`/knowledge/estate/concepts/${concept.id}`}
          className="text-[10px] text-brand-teal hover:text-brand-teal-dark inline-flex items-center gap-0.5"
        >
          open
          <ExternalLink size={10} />
        </Link>
      </div>
      <div className="flex items-center gap-1.5 mb-2 text-[10px] text-slate-500">
        <span className="px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600">
          {concept.entity_type}
        </span>
        <span>v{concept.version}</span>
        {concept.confidence != null && (
          <span>conf {(concept.confidence * 100).toFixed(0)}%</span>
        )}
      </div>
      {entries.length > 0 && (
        <div className="space-y-0.5 text-[11px] text-slate-600">
          {entries.map(([k, v]) => (
            <div key={k} className="flex gap-2">
              <span className="text-slate-400 min-w-[6rem] truncate">{k}</span>
              <span className="truncate">
                {typeof v === 'object' ? JSON.stringify(v) : String(v)}
              </span>
            </div>
          ))}
        </div>
      )}
      {concept.relations && concept.relations.length > 0 && (
        <div className="mt-2 text-[10px] text-slate-500">
          {concept.relations.length} relations
        </div>
      )}
    </div>
  );
}
